import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProjectReviewPage() {
  const [draft, setDraft] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const raw = sessionStorage.getItem("halfjob_draft");
    if (!raw) {
      navigate("/projects/new", { replace: true });
      return;
    }
    setDraft(JSON.parse(raw));
  }, [navigate]);

  if (!draft) return null;

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const updateStep = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      steps: d.steps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  const addStep = () =>
    setDraft((d) => ({
      ...d,
      steps: [...d.steps, { order: (d.steps.at(-1)?.order ?? 0) + 10, title: "", notes: "" }],
    }));

  const removeStep = (i) =>
    setDraft((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }));

  const updateMaterial = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      materials: d.materials.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)),
    }));

  const addMaterial = () =>
    setDraft((d) => ({
      ...d,
      materials: [...d.materials, { name: "", quantity: "", confidence: null, purchase_suggestion: null }],
    }));

  const removeMaterial = (i) =>
    setDraft((d) => ({ ...d, materials: d.materials.filter((_, idx) => idx !== i) }));

  const goToMaterialsConfirm = () => {
    if (!draft.title?.trim()) return toast.error("Add a title");
    if (draft.steps.length === 0) return toast.error("Add at least one step");
    sessionStorage.setItem("halfjob_draft", JSON.stringify({
      ...draft,
      steps: draft.steps.filter((s) => s.title.trim()),
      materials: draft.materials.filter((m) => m.name.trim()),
    }));
    navigate("/projects/new/materials");
  };

  return (
    <div className="min-h-screen pb-32" data-testid="project-review-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="review-back">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <p className="label-cap mt-3">Step 2 of 4 · Review draft</p>
        <Input
          value={draft.title}
          onChange={(e) => updateField("title", e.target.value)}
          data-testid="review-title-input"
          className="font-heading text-2xl font-bold border-0 px-0 bg-transparent mt-1 focus-visible:ring-0"
        />
        <div className="mt-2 bg-kraft border-2 border-stone_border rounded-xl p-3 text-xs text-timber-900 leading-snug" data-testid="review-draft-banner">
          <strong className="font-bold">This is a draft proposal from the AI.</strong>{" "}
          Edit steps and materials freely — next you'll confirm what you already have and what's missing before the project is created.
        </div>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-6">
        {/* META */}
        <div className="grid grid-cols-3 gap-2">
          <MetaBox label="Difficulty" value={draft.difficulty || "—"} />
          <MetaBox label="Est. time" value={draft.estimated_minutes ? `${draft.estimated_minutes} min` : "—"} />
          <MetaBox label="Confidence" value={draft.confidence ? `${Math.round(draft.confidence * 100)}%` : "—"} />
        </div>

        {draft.safety_warnings?.length > 0 && (
          <div className="tactile-card p-4 bg-[#FBEDEE] border-block">
            <div className="flex items-center gap-2 text-block font-bold">
              <Warning size={20} weight="fill" /> Safety
            </div>
            <ul className="mt-2 text-sm text-timber-900 list-disc pl-5 space-y-1">
              {draft.safety_warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* STEPS */}
        <section data-testid="review-steps-section">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h2 className="font-heading text-xl font-bold">Steps</h2>
              <p className="text-xs text-timber-700">Small actions in order</p>
            </div>
            <button
              onClick={addStep}
              data-testid="review-add-step"
              className="text-masking font-bold flex items-center gap-1 text-sm"
            >
              <Plus size={16} weight="bold" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {draft.steps.map((s, i) => (
              <div
                key={i}
                className="tactile-card p-3 flex items-start gap-2"
                data-testid={`review-step-${i}`}
              >
                <div className="font-mono text-xs text-timber-500 pt-3 w-6">{i + 1}.</div>
                <div className="flex-1 space-y-1">
                  <Input
                    value={s.title}
                    onChange={(e) => updateStep(i, "title", e.target.value)}
                    placeholder="Step title"
                    className="min-h-[44px] bg-chalk border border-stone_border rounded-lg"
                  />
                  {s.notes && (
                    <Input
                      value={s.notes}
                      onChange={(e) => updateStep(i, "notes", e.target.value)}
                      placeholder="Notes"
                      className="min-h-[36px] bg-chalk border border-stone_border rounded-lg text-sm"
                    />
                  )}
                </div>
                <button onClick={() => removeStep(i)} data-testid={`review-step-remove-${i}`} className="text-timber-500 p-2">
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* MATERIALS */}
        <section data-testid="review-materials-section">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h2 className="font-heading text-xl font-bold">Materials</h2>
              <p className="text-xs text-timber-700">Edit, remove, or add</p>
            </div>
            <button
              onClick={addMaterial}
              data-testid="review-add-material"
              className="text-masking font-bold flex items-center gap-1 text-sm"
            >
              <Plus size={16} weight="bold" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {draft.materials.map((m, i) => (
              <div key={i} className="tactile-card p-3" data-testid={`review-material-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <Input
                      value={m.name}
                      onChange={(e) => updateMaterial(i, "name", e.target.value)}
                      placeholder="Item"
                      className="min-h-[44px] col-span-2 bg-chalk border border-stone_border rounded-lg"
                    />
                    <Input
                      value={m.quantity || ""}
                      onChange={(e) => updateMaterial(i, "quantity", e.target.value)}
                      placeholder="Qty"
                      className="min-h-[44px] bg-chalk border border-stone_border rounded-lg"
                    />
                  </div>
                  <button onClick={() => removeMaterial(i)} data-testid={`review-material-remove-${i}`} className="text-timber-500 p-2">
                    <Trash size={16} />
                  </button>
                </div>
                {(m.purchase_suggestion?.recommended?.product_name || m.purchase_suggestion?.product_type) && (
                  <p className="text-xs text-timber-700 mt-1 font-mono">
                    Buy: {m.purchase_suggestion.recommended?.product_name || m.purchase_suggestion.product_type}
                    {(m.purchase_suggestion.retailers?.[0] || m.purchase_suggestion.retailer_category) &&
                      ` · ${m.purchase_suggestion.retailers?.[0] || m.purchase_suggestion.retailer_category}`}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <Button
          data-testid="review-save-btn"
          onClick={goToMaterialsConfirm}
          className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[64px] text-lg font-bold btn-press"
        >
          Looks Good → Next
        </Button>
      </main>
    </div>
  );
}

function MetaBox({ label, value }) {
  return (
    <div className="tactile-card p-3 text-center">
      <div className="label-cap text-[10px]">{label}</div>
      <div className="font-heading text-base font-bold text-timber-900 mt-0.5 capitalize">
        {value}
      </div>
    </div>
  );
}
